import React, {FC, memo, useEffect, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CleanHtml} from '../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {TimelineVariationsEnum} from '../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemSecondaryContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemSecondaryContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SignatureModel} from '../../../../models/api/signatureModel';
import {getHasEnforcedCompanySignatures} from '../../../../store/data/company/companySelectors';
import {ComposerSectionButton} from '../common/composerSectionButton';
import {ComposerSectionEditorCoordinator} from '../common/composerSectionEditorCoordinator';
import {EmailComposerStatic} from '../common/emailComposerStatic';

/*
 * Constants.
 */

const dropdownWidth = 250;

/*
 * Props.
 */

export interface SignaturesProps {
  message: DraftMessageModel;
  availableSignatures: ReadonlyArray<SignatureModel>;
  /** Interpolated signature HTML. */
  signatureHtml?: CleanHtml;
  isReadonly?: boolean;
  isAlwaysEditing: boolean;
  editSignature: () => void;
  revertSignature: () => void;
  removeSignature: () => void;
  addSignature: () => void;
  replaceSignature: (arg: number) => void;
  renderEditor: Renderer;
  hasFocus?: boolean;
}

interface SignaturesReplaceButtonProps {
  availableSignatures: ReadonlyArray<SignatureModel>;
  selectedSignatureId: number | undefined;
  replaceSignature: (arg: number) => void;
  onDropdownToggle?: (arg: boolean) => void;
}

/*
 * Component.
 */

export const Signatures: FC<SignaturesProps> = memo((props) => {
  const [isReplaceDropdownOpen, setIsReplaceDropdownOpen] = useState(false);

  const {message, availableSignatures, isAlwaysEditing, renderEditor, hasFocus, isReadonly} = props;

  const hasEnforcedCompanySignatures = useAppSelector(getHasEnforcedCompanySignatures);
  // If company signatures are enforced, and the message has no signatures, but there is one available, automatically add it.
  useEffect(() => {
    if (hasEnforcedCompanySignatures && !message.isSignatureIncluded && availableSignatures.length > 0) {
      props.replaceSignature(availableSignatures[0].id);
    }
  }, [availableSignatures.length]);

  if (availableSignatures.length === 0) {
    return null;
  }

  const composerSection = (
    <ComposerSectionEditorCoordinator
      isIncluded={Boolean(message.isSignatureIncluded)}
      onAdd={props.addSignature}
      onRemove={props.removeSignature}
      renderAddLabel={() => <Trans id="composer-signature-add">Add signature</Trans>}
      renderRemoveLabel={() => <Trans id="composer-signature-remove">Remove signature</Trans>}
      renderStatic={() => (
        <EmailComposerStatic
          isReadonly={isReadonly}
          html={props.signatureHtml}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        />
      )}
      isAlwaysEditing={isAlwaysEditing}
      isReadonly={isReadonly}
      isEditing={Boolean(message.wasSignatureModified)}
      onEdit={props.editSignature}
      onRevert={props.revertSignature}
      renderEditor={renderEditor}
      hasHover={isReplaceDropdownOpen}
      hasFocus={hasFocus}
      renderExtraRightActions={() => (
        <SignaturesReplaceButton
          availableSignatures={availableSignatures}
          selectedSignatureId={message.signatureId}
          replaceSignature={props.replaceSignature}
          onDropdownToggle={setIsReplaceDropdownOpen}
        />
      )}
    />
  );

  if (isReadonly) {
    return (
      <TooltipCoordinator
        placement="top-start"
        customMargin={5}
        hideOnMouseDown
        render={() => (
          <Trans id="signatures-composer-enforced-signature-tooltip">
            Personal signatures and signature editing disabled by administrator.
          </Trans>
        )}
      >
        {composerSection}
      </TooltipCoordinator>
    );
  }

  return composerSection;
});

const SignaturesReplaceButton: FC<SignaturesReplaceButtonProps> = (props) => {
  // If there's only signature, it's already being displayed.
  if (props.availableSignatures.length === 1) {
    return null;
  }

  // Otherwise, render the button.
  return (
    <PopoverCoordinator
      onPopoverToggle={props.onDropdownToggle}
      renderButton={renderReplaceButton}
      renderModal={(modalProps) => renderReplaceDropdown(modalProps, props)}
    />
  );
};

function renderReplaceButton(isOpen: boolean) {
  return (
    <div>
      <TooltipCoordinator render={() => <Trans id="composer-signature-replace">Switch signature</Trans>}>
        <ComposerSectionButton hasHover={isOpen}>
          <Icon name="arrowsTiny" />
          <VisuallyHidden>
            <Trans id="composer-signature-replace">Switch signature</Trans>
          </VisuallyHidden>
        </ComposerSectionButton>
      </TooltipCoordinator>
    </div>
  );
}

function renderReplaceDropdown(modalProps: PopoverTransferProps, props: SignaturesReplaceButtonProps) {
  const {selectedSignatureId, availableSignatures, replaceSignature} = props;

  // Render the items.
  const dropdownItems = availableSignatures.map((s) => {
    const isChecked = selectedSignatureId === s.id;
    const title = s.name || s.senderInfo;

    return (
      <DropdownItem key={s.id} onSelect={() => replaceSignature(s.id)} isChecked={isChecked}>
        <DropdownItemContent isChecked={isChecked}>{title}</DropdownItemContent>
        <DropdownItemSecondaryContent>{s.blurb}</DropdownItemSecondaryContent>
      </DropdownItem>
    );
  });

  // Render the dropdown.
  return (
    <SimpleDropdown {...modalProps} placement="bottom-end" width={dropdownWidth}>
      {dropdownItems}
    </SimpleDropdown>
  );
}
